/**
 * Configuration Validation Utilities
 *
 * Validates merchant configuration structure. Findings are split into two
 * severities so that a translation gap can never knock out a merchant's
 * whole flow:
 *
 * - errors:   structural problems (missing sections, invalid types/enums,
 *             duplicate ids/orders, required texts empty in every language).
 *             The app discards the config and falls back to the default flow.
 * - warnings: translation gaps (missing mandatory-language text, optional
 *             texts empty everywhere). The config still runs; rendering
 *             falls back per text (see resolveConfigText in i18n.js).
 *
 * Every finding is { path, code, message } so consumers can anchor it to a
 * field (path), translate it (code), or log it (message). This shape and the
 * (config, options) signature are the shared-contract API agreed with the
 * admin panel; keep them stable.
 *
 * options:
 * - ruleset: 'en-only' (default) — non-empty "en" is the only mandatory
 *            language, enforced as a warning.
 *            'de-en' — transitional legacy contract: non-empty "de" AND "en"
 *            required as errors (pre-relaxation behavior, used by the admin
 *            panel during the rollout window).
 * - merchantLanguages: optional array of enabled language codes; texts
 *            missing one of them produce a warning. Omit to skip this check
 *            (the return app omits it — it matters at authoring time).
 */

const VALID_SECTION_TYPES = ['photo_upload', 'question_set', 'completion'];
const VALID_QUESTION_TYPES = ['radio', 'checkbox', 'text', 'textarea'];
const VALID_ACTIONS = ['proceed', 'block', 'require_details'];
const VALID_PHOTO_MODES = ['optional', 'required', 'required_if_issues'];
const VALID_PAGINATION_MODES = ['one_per_page', 'accordion', 'all_at_once'];
const VALID_ITEM_STATUS = ['ok', 'issue', 'damaged', 'missing'];
const VALID_OVERALL_STATUS = ['all_ok', 'issues_reported'];

const nonEmpty = (value) => typeof value === 'string' && value.trim() !== '';

const ConfigValidator = {
  /**
   * Validate complete flow configuration
   * @param {Object} config - Complete merchant configuration
   * @param {Object} [options] - { ruleset, merchantLanguages } (see header)
   * @returns {{errors: Array, warnings: Array}} findings as {path, code, message}
   */
  validateFlowConfig(config, options = {}) {
    const ctx = {
      errors: [],
      warnings: [],
      ruleset: options.ruleset === 'de-en' ? 'de-en' : 'en-only',
      merchantLanguages: Array.isArray(options.merchantLanguages)
        ? options.merchantLanguages
        : null,
    };

    if (!config?.flow_config) {
      ctx.errors.push({
        path: 'flow_config',
        code: 'missing_flow_config',
        message: 'Missing flow_config',
      });
      return { errors: ctx.errors, warnings: ctx.warnings };
    }

    const sections = config.flow_config.sections;
    if (!Array.isArray(sections)) {
      ctx.errors.push({
        path: 'flow_config.sections',
        code: 'sections_not_array',
        message: 'flow_config.sections must be an array',
      });
      return { errors: ctx.errors, warnings: ctx.warnings };
    }

    if (!sections.some(s => s?.type === 'completion')) {
      ctx.errors.push({
        path: 'flow_config.sections',
        code: 'missing_completion',
        message: 'Must have at least one "completion" section',
      });
    }

    sections.forEach((section, index) => {
      this.validateSection(section, `sections[${index}]`, ctx);
    });

    const orders = sections.map(s => s?.order);
    if (orders.length !== new Set(orders).size) {
      ctx.errors.push({
        path: 'flow_config.sections',
        code: 'duplicate_orders',
        message: 'Section orders must be unique',
      });
    }

    const ids = sections.map(s => s?.id);
    if (ids.length !== new Set(ids).size) {
      ctx.errors.push({
        path: 'flow_config.sections',
        code: 'duplicate_ids',
        message: 'Section IDs must be unique',
      });
    }

    return { errors: ctx.errors, warnings: ctx.warnings };
  },

  /**
   * Validate a single section
   */
  validateSection(section, path, ctx) {
    const label = `${path} (${section?.id || 'unnamed'})`;

    if (!section || typeof section !== 'object') {
      ctx.errors.push({
        path,
        code: 'section_invalid',
        message: `${label}: must be an object`,
      });
      return;
    }

    if (!section.id) {
      ctx.errors.push({ path: `${path}.id`, code: 'missing_id', message: `${label}: missing id` });
    }
    if (!section.type) {
      ctx.errors.push({ path: `${path}.type`, code: 'missing_type', message: `${label}: missing type` });
    }
    if (section.order === undefined || section.order === null) {
      ctx.errors.push({ path: `${path}.order`, code: 'missing_order', message: `${label}: missing order` });
    }
    if (section.enabled === undefined) {
      ctx.errors.push({ path: `${path}.enabled`, code: 'missing_enabled', message: `${label}: missing enabled flag` });
    }

    if (section.type && !VALID_SECTION_TYPES.includes(section.type)) {
      ctx.errors.push({
        path: `${path}.type`,
        code: 'invalid_section_type',
        message: `${label}: invalid type "${section.type}". Must be one of: ${VALID_SECTION_TYPES.join(', ')}`,
      });
    }

    if (section.type === 'photo_upload') {
      this.validatePhotoSection(section, path, label, ctx);
    } else if (section.type === 'question_set') {
      this.validateQuestionSection(section, path, label, ctx);
    } else if (section.type === 'completion') {
      this.validateCompletionSection(section, path, label, ctx);
    }
  },

  /**
   * Validate photo upload section
   */
  validatePhotoSection(section, path, label, ctx) {
    const config = section.config;

    if (!config) {
      ctx.errors.push({ path: `${path}.config`, code: 'missing_config', message: `${label}: missing config` });
      return;
    }

    if (config.min_photos !== undefined && config.min_photos < 0) {
      ctx.errors.push({
        path: `${path}.config.min_photos`,
        code: 'min_photos_negative',
        message: `${label}: min_photos cannot be negative`,
      });
    }
    if (config.max_photos !== undefined && config.max_photos < 0) {
      ctx.errors.push({
        path: `${path}.config.max_photos`,
        code: 'max_photos_negative',
        message: `${label}: max_photos cannot be negative`,
      });
    }
    if (config.min_photos !== undefined && config.max_photos !== undefined &&
        config.max_photos < config.min_photos) {
      ctx.errors.push({
        path: `${path}.config.max_photos`,
        code: 'max_below_min_photos',
        message: `${label}: max_photos must be >= min_photos`,
      });
    }
    if (config.max_photos > 50) {
      ctx.errors.push({
        path: `${path}.config.max_photos`,
        code: 'max_photos_exceeded',
        message: `${label}: max_photos cannot exceed 50 (performance limit)`,
      });
    }

    if (config.header_text) {
      this.validateTextObject(config.header_text, `${path}.config.header_text`, ctx);
    }
    if (config.instructions) {
      this.validateTextObject(config.instructions, `${path}.config.instructions`, ctx);
    }

    if (config.scope && !['booking', 'item'].includes(config.scope)) {
      ctx.errors.push({
        path: `${path}.config.scope`,
        code: 'invalid_scope',
        message: `${label}: scope must be "booking" or "item"`,
      });
    }
  },

  /**
   * Validate question set section
   */
  validateQuestionSection(section, path, label, ctx) {
    const config = section.config;

    if (!config) {
      ctx.errors.push({ path: `${path}.config`, code: 'missing_config', message: `${label}: missing config` });
      return;
    }

    if (!config.scope) {
      ctx.errors.push({ path: `${path}.config.scope`, code: 'missing_scope', message: `${label}: missing scope` });
    } else if (!['booking', 'item'].includes(config.scope)) {
      ctx.errors.push({
        path: `${path}.config.scope`,
        code: 'invalid_scope',
        message: `${label}: scope must be "booking" or "item"`,
      });
    }

    if (!Array.isArray(config.questions)) {
      ctx.errors.push({
        path: `${path}.config.questions`,
        code: 'questions_not_array',
        message: `${label}: config.questions must be an array`,
      });
      return;
    }

    const maxQuestions = 5;
    if (config.questions.length > maxQuestions) {
      ctx.errors.push({
        path: `${path}.config.questions`,
        code: 'too_many_questions',
        message: `${label}: too many questions (max ${maxQuestions} for ${config.scope} scope)`,
      });
    }

    config.questions.forEach((question, qIndex) => {
      this.validateQuestion(question, `${path}.config.questions[${qIndex}]`, ctx);
    });

    if (config.scope === 'item') {
      if (config.pagination && !VALID_PAGINATION_MODES.includes(config.pagination)) {
        ctx.errors.push({
          path: `${path}.config.pagination`,
          code: 'invalid_pagination',
          message: `${label}: invalid pagination mode "${config.pagination}". Must be one of: ${VALID_PAGINATION_MODES.join(', ')}`,
        });
      }

      if (config.item_photos_config) {
        const photoConfig = config.item_photos_config;
        const photoPath = `${path}.config.item_photos_config`;
        if (photoConfig.mode && !VALID_PHOTO_MODES.includes(photoConfig.mode)) {
          ctx.errors.push({
            path: `${photoPath}.mode`,
            code: 'invalid_photo_mode',
            message: `${label}: invalid item_photos_config.mode "${photoConfig.mode}". Must be one of: ${VALID_PHOTO_MODES.join(', ')}`,
          });
        }
        if (photoConfig.min_photos !== undefined && photoConfig.min_photos < 0) {
          ctx.errors.push({
            path: `${photoPath}.min_photos`,
            code: 'min_photos_negative',
            message: `${label}.item_photos_config: min_photos cannot be negative`,
          });
        }
        if (photoConfig.max_photos !== undefined && photoConfig.max_photos < 0) {
          ctx.errors.push({
            path: `${photoPath}.max_photos`,
            code: 'max_photos_negative',
            message: `${label}.item_photos_config: max_photos cannot be negative`,
          });
        }
      }
    }

    if (config.header_text) {
      this.validateTextObject(config.header_text, `${path}.config.header_text`, ctx);
    }
    if (config.instructions) {
      this.validateTextObject(config.instructions, `${path}.config.instructions`, ctx);
    }
  },

  /**
   * Validate a single question
   */
  validateQuestion(question, path, ctx) {
    if (!question.id) {
      ctx.errors.push({ path: `${path}.id`, code: 'missing_id', message: `${path}: missing id` });
    }
    if (!question.text) {
      ctx.errors.push({ path: `${path}.text`, code: 'missing_text', message: `${path}: missing text` });
    }
    if (!question.type) {
      ctx.errors.push({ path: `${path}.type`, code: 'missing_type', message: `${path}: missing type` });
    }
    if (question.order === undefined) {
      ctx.errors.push({ path: `${path}.order`, code: 'missing_order', message: `${path}: missing order` });
    }

    if (question.type && !VALID_QUESTION_TYPES.includes(question.type)) {
      ctx.errors.push({
        path: `${path}.type`,
        code: 'invalid_question_type',
        message: `${path}: invalid type "${question.type}". Must be one of: ${VALID_QUESTION_TYPES.join(', ')}`,
      });
    }

    if (question.text) {
      // A question with no text in any language is unanswerable — hard error.
      this.validateTextObject(question.text, `${path}.text`, ctx, { requireContent: true });
    }
    if (question.preamble) {
      this.validateTextObject(question.preamble, `${path}.preamble`, ctx);
    }

    if (['radio', 'checkbox'].includes(question.type)) {
      if (!Array.isArray(question.options) || question.options.length === 0) {
        ctx.errors.push({
          path: `${path}.options`,
          code: 'missing_options',
          message: `${path}: must have at least one option for type "${question.type}"`,
        });
        return;
      }

      question.options.forEach((option, oIndex) => {
        this.validateOption(option, `${path}.options[${oIndex}]`, ctx);
      });

      const values = question.options.map(o => o.value);
      if (values.length !== new Set(values).size) {
        ctx.errors.push({
          path: `${path}.options`,
          code: 'duplicate_option_values',
          message: `${path}: option values must be unique`,
        });
      }
    }
  },

  /**
   * Validate a question option
   */
  validateOption(option, path, ctx) {
    if (!option.value) {
      ctx.errors.push({ path: `${path}.value`, code: 'missing_value', message: `${path}: missing value` });
    }
    if (!option.label) {
      ctx.errors.push({ path: `${path}.label`, code: 'missing_label', message: `${path}: missing label` });
    }
    if (!option.action) {
      ctx.errors.push({ path: `${path}.action`, code: 'missing_action', message: `${path}: missing action` });
    }

    if (option.action && !VALID_ACTIONS.includes(option.action)) {
      ctx.errors.push({
        path: `${path}.action`,
        code: 'invalid_action',
        message: `${path}: invalid action "${option.action}". Must be one of: ${VALID_ACTIONS.join(', ')}`,
      });
    }

    if (option.label) {
      // An option the user cannot read is unusable — hard error when blank everywhere.
      this.validateTextObject(option.label, `${path}.label`, ctx, { requireContent: true });
    }

    if (option.action === 'block') {
      if (!option.blocking_message) {
        ctx.errors.push({
          path: `${path}.blocking_message`,
          code: 'missing_blocking_message',
          message: `${path}: action "block" requires blocking_message`,
        });
      } else {
        this.validateTextObject(option.blocking_message, `${path}.blocking_message`, ctx, { requireContent: true });
      }
    }

    if (option.action === 'require_details') {
      if (!option.details_config) {
        ctx.errors.push({
          path: `${path}.details_config`,
          code: 'missing_details_config',
          message: `${path}: action "require_details" requires details_config`,
        });
      } else {
        if (!option.details_config.field_name) {
          ctx.errors.push({
            path: `${path}.details_config.field_name`,
            code: 'missing_field_name',
            message: `${path}.details_config: missing field_name`,
          });
        }
        if (option.details_config.placeholder) {
          this.validateTextObject(option.details_config.placeholder, `${path}.details_config.placeholder`, ctx);
        }
      }
    }
  },

  /**
   * Validate completion section
   */
  validateCompletionSection(section, path, label, ctx) {
    const config = section.config;

    if (!config) {
      ctx.errors.push({ path: `${path}.config`, code: 'missing_config', message: `${label}: missing config` });
      return;
    }

    if (config.header_text) {
      this.validateTextObject(config.header_text, `${path}.config.header_text`, ctx);
    }
    if (config.message) {
      this.validateTextObject(config.message, `${path}.config.message`, ctx);
    }

    if (config.review_request) {
      if (config.review_request.text) {
        this.validateTextObject(config.review_request.text, `${path}.config.review_request.text`, ctx);
      }
      if (config.review_request.button_text) {
        this.validateTextObject(config.review_request.button_text, `${path}.config.review_request.button_text`, ctx);
      }
    }

    if (config.instruction_boxes) {
      if (!Array.isArray(config.instruction_boxes)) {
        ctx.errors.push({
          path: `${path}.config.instruction_boxes`,
          code: 'instruction_boxes_not_array',
          message: `${label}: instruction_boxes must be an array`,
        });
        return;
      }

      if (config.instruction_boxes.length > 3) {
        ctx.errors.push({
          path: `${path}.config.instruction_boxes`,
          code: 'too_many_instruction_boxes',
          message: `${label}: maximum 3 instruction boxes allowed`,
        });
      }

      config.instruction_boxes.forEach((box, bIndex) => {
        const bPath = `${path}.config.instruction_boxes[${bIndex}]`;

        if (box.title) {
          this.validateTextObject(box.title, `${bPath}.title`, ctx);
        }
        if (box.text) {
          this.validateTextObject(box.text, `${bPath}.text`, ctx);
        }
        if (box.order === undefined) {
          ctx.errors.push({ path: `${bPath}.order`, code: 'missing_order', message: `${bPath}: missing order` });
        }
        if (box.location_ids !== undefined) {
          if (!Array.isArray(box.location_ids)) {
            ctx.errors.push({
              path: `${bPath}.location_ids`,
              code: 'location_ids_not_array',
              message: `${bPath}.location_ids: must be an array`,
            });
          } else if (!box.location_ids.every(id => typeof id === 'string' && id.trim() !== '')) {
            ctx.errors.push({
              path: `${bPath}.location_ids`,
              code: 'invalid_location_ids',
              message: `${bPath}.location_ids: must contain non-empty strings`,
            });
          }
        }
      });
    }
  },

  /**
   * Validate a localized text object.
   *
   * - Not an object → error.
   * - Empty in every language → error when requireContent (question texts,
   *   option labels, blocking messages — nothing to fall back to), otherwise
   *   warning (headers/instructions have built-in or empty fallbacks).
   * - Mandatory language missing while other content exists → warning under
   *   'en-only' (runtime falls back), error under the transitional 'de-en'
   *   ruleset (legacy contract).
   * - Merchant-enabled language missing → warning (only when
   *   merchantLanguages was provided).
   */
  validateTextObject(text, path, ctx, { requireContent = false } = {}) {
    if (typeof text !== 'object' || text === null || Array.isArray(text)) {
      ctx.errors.push({
        path,
        code: 'text_invalid',
        message: `${path}: must be an object mapping language codes to strings`,
      });
      return;
    }

    const hasAnyContent = Object.values(text).some(nonEmpty);
    if (!hasAnyContent) {
      const finding = {
        path,
        code: 'text_empty',
        message: `${path}: has no text in any language`,
      };
      (requireContent ? ctx.errors : ctx.warnings).push(finding);
      return;
    }

    const mandatory = ctx.ruleset === 'de-en' ? ['de', 'en'] : ['en'];
    for (const lang of mandatory) {
      if (!nonEmpty(text[lang])) {
        const finding = {
          path,
          code: 'missing_translation',
          message: `${path}: missing or empty "${lang}" text`,
        };
        (ctx.ruleset === 'de-en' ? ctx.errors : ctx.warnings).push(finding);
      }
    }

    if (ctx.merchantLanguages) {
      for (const lang of ctx.merchantLanguages) {
        if (!mandatory.includes(lang) && !nonEmpty(text[lang])) {
          ctx.warnings.push({
            path,
            code: 'missing_language',
            message: `${path}: missing "${lang}" text (merchant-enabled language)`,
          });
        }
      }
    }
  },

  /**
   * Validate item status value
   */
  validateItemStatus(status) {
    if (!VALID_ITEM_STATUS.includes(status)) {
      return `Invalid item_status "${status}". Must be one of: ${VALID_ITEM_STATUS.join(', ')}`;
    }
    return null;
  },

  /**
   * Validate overall status value
   */
  validateOverallStatus(status) {
    if (!VALID_OVERALL_STATUS.includes(status)) {
      return `Invalid overall_status "${status}". Must be one of: ${VALID_OVERALL_STATUS.join(', ')}`;
    }
    return null;
  }
};

export default ConfigValidator;
